import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis,
  Tooltip, ReferenceLine, ReferenceDot,
} from 'recharts';
import { format, differenceInWeeks } from 'date-fns';
import { Activity, TrendingUp, AlertTriangle, Zap, Hourglass } from 'lucide-react';
import useMarketData from '../hooks/useMarketData.js';

/**
 * WeeklyCycleSignal
 * -----------------
 * Visualises the user's backtested rule:
 *   "On the weekly Heikin Ashi chart, when RSI(14) drops below 30 and then
 *    closes back above 50, BTC has historically not made a new bottom and
 *    has gone on to make new ATHs."
 *
 * Three states the widget can be in:
 *   - INACTIVE — RSI hasn't dipped below 30 in the recent past
 *   - ARMED    — RSI is currently below 50 (or recently was below 30 but
 *                hasn't yet closed back above 50). The setup is in play.
 *   - TRIGGERED — RSI has closed above 50 after being below 30. Historically
 *                this has marked a cycle bottom and the start of a new
 *                trend higher.
 *
 * Honest framing in the disclaimer: 3 historical triggers in 16 years is a
 * small sample, and "no new bottom" is a backwards-looking observation, not
 * a forward-looking promise. The widget surfaces the math; the user decides.
 */
export default function WeeklyCycleSignal() {
  const { btcWeekly } = useMarketData();
  const bars = btcWeekly?.bars ?? [];

  const analysis = useMemo(() => analyseWeekly(bars), [bars]);

  // Loading / error states
  if (btcWeekly?.status === 'loading' && !bars.length) {
    return <Skeleton />;
  }
  if (!analysis) {
    return (
      <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 md:p-6">
        <div className="flex items-center gap-2 text-slate-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm">Weekly cycle data unavailable</span>
        </div>
      </section>
    );
  }

  const {
    state, currentRsi, currentBar, triggers, chartData,
    weeksSinceArmed, weeksSinceLastTrigger, activeWindowLow, armDipDate,
  } = analysis;
  const stateConfig = STATE_CONFIG[state];

  return (
    <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-purple-500/[0.04] via-slate-900/60 to-pink-500/[0.04] backdrop-blur-xl p-4 md:p-6">
      <div className="absolute -top-24 -right-24 w-72 h-72 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-400" />
              Weekly Cycle Signal — Heikin Ashi RSI
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Tracks the "RSI(14) dipped below 30 → closed above 50" weekly Heikin Ashi pattern.
              Historically marks cycle bottoms — never a guarantee.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">As of</div>
            <div className="text-xs text-slate-300 font-mono tabular-nums">
              {currentBar?.date ? format(currentBar.date, 'MMM d, yyyy') : '—'}
            </div>
          </div>
        </div>

        {/* Main state indicator */}
        <StateIndicator
          config={stateConfig}
          rsi={currentRsi}
          weeksSinceArmed={weeksSinceArmed}
          weeksSinceLastTrigger={weeksSinceLastTrigger}
          activeWindowLow={activeWindowLow}
          armDipDate={armDipDate || (state === 'TRIGGERED' ? triggers[triggers.length - 1]?.dipDate : null)}
          triggerDate={state === 'TRIGGERED' ? triggers[triggers.length - 1]?.date : null}
        />

        {/* RSI scale + chart */}
        <RsiScale rsi={currentRsi} />
        <RsiChart data={chartData} triggers={triggers} />

        {/* Past triggers history */}
        {triggers.length > 0 && (
          <TriggerHistory triggers={triggers} latestPrice={currentBar?.close} />
        )}

        <p className="text-[11px] text-slate-500 italic mt-4 leading-relaxed">
          <strong className="text-slate-400 not-italic">Methodology:</strong>{' '}
          weekly OHLC from Yahoo Finance → Heikin Ashi candles via the standard
          recurrence (HA close = (O+H+L+C)/4) → RSI(14) on HA closes using
          Wilder's smoothing. A "trigger" fires the first weekly close where
          RSI &gt; 50 after the prior 12 weeks contained a sub-30 dip.
          {' '}<strong className="text-slate-400 not-italic">Caveat:</strong>{' '}
          three trigger events in 16 years is a tiny sample. "No new bottom"
          is a backwards-looking observation, not a forward-looking promise.
          Past performance does not guarantee future results.
        </p>
      </div>
    </section>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function StateIndicator({ config, rsi, weeksSinceArmed, weeksSinceLastTrigger, activeWindowLow, armDipDate, triggerDate }) {
  const { Icon, label, ring, glow, text, sub } = config;
  return (
    <div className={`relative rounded-xl border ${ring} ${glow} px-4 py-4 mb-5 overflow-hidden`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full ${ring} flex items-center justify-center bg-slate-950/40`}>
            <Icon className={`w-6 h-6 ${text}`} />
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-widest ${text} font-bold`}>Current state</div>
            <div className={`text-2xl font-bold ${text} leading-tight`}>{label}</div>
            <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>
          </div>
        </div>
        {Number.isFinite(rsi) && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">HA RSI(14)</div>
            <div className={`text-3xl md:text-4xl font-bold tabular-nums ${text}`}>
              {rsi.toFixed(1)}
            </div>
            {(weeksSinceArmed != null || weeksSinceLastTrigger != null) && (
              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                {weeksSinceArmed != null && `Armed ${weeksSinceArmed}w`}
                {weeksSinceArmed == null && weeksSinceLastTrigger != null
                  && `Last trigger ${weeksSinceLastTrigger}w ago`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Key dates: when RSI dipped below 30 and when it closed above 50 */}
      {(armDipDate || triggerDate) && (
        <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {armDipDate && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">RSI dipped below 30</div>
              <div className="text-sm font-mono tabular-nums text-slate-200 mt-0.5">
                {format(armDipDate, 'MMM d, yyyy')}
              </div>
            </div>
          )}
          {triggerDate ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">RSI closed above 50</div>
              <div className="text-sm font-mono tabular-nums text-slate-200 mt-0.5">
                {format(triggerDate, 'MMM d, yyyy')}
              </div>
            </div>
          ) : armDipDate && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">RSI closed above 50</div>
              <div className="text-sm text-slate-500 mt-0.5">Waiting…</div>
            </div>
          )}
        </div>
      )}

      {/* Active cycle-low strip — only renders while ARMED. Shows the lowest
          BTC price seen since RSI dipped under 30, which is the answer to
          "where did this cycle bottom?" If the trigger fires, this number
          is the cycle low the rule's "no new bottom" claim is anchored to. */}
      {activeWindowLow && (
        <div className="mt-4 pt-3 border-t border-amber-500/20 flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              Cycle low so far
            </div>
            <div className="text-[11px] text-slate-500">
              Lowest BTC price since RSI fell under 30
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-amber-200 tabular-nums leading-tight">
              ${Math.round(activeWindowLow.low).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-500 font-mono">
              week of {format(activeWindowLow.date, 'MMM d, yyyy')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RsiScale({ rsi }) {
  if (!Number.isFinite(rsi)) return null;
  const pct = Math.max(0, Math.min(100, rsi));
  return (
    <div className="mb-2">
      <div className="relative h-7">
        {/* Three-zone gradient: red <30, neutral 30-50, green >50 */}
        <div className="absolute inset-0 flex rounded overflow-hidden">
          <div className="bg-red-500/40"     style={{ width: '30%' }} />
          <div className="bg-amber-500/30"   style={{ width: '20%' }} />
          <div className="bg-emerald-500/40" style={{ width: '50%' }} />
        </div>
        {/* Marker */}
        <div
          className="absolute top-0 h-full w-0.5 bg-white shadow-[0_0_10px_rgba(255,255,255,0.85)]"
          style={{ left: `${pct}%`, transform: 'translateX(-50%)', transition: 'left 0.6s ease-out' }}
        >
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-md tabular-nums shadow-lg">
            {rsi.toFixed(1)}
          </div>
        </div>
        {/* 30 / 50 / 70 reference labels */}
        <div className="absolute inset-x-0 bottom-0 flex text-[8px] font-mono text-slate-500/80">
          <div style={{ width: '30%' }} className="text-right pr-1">30</div>
          <div style={{ width: '20%' }} className="text-right pr-1">50</div>
          <div style={{ width: '20%' }} className="text-right pr-1">70</div>
          <div style={{ width: '30%' }} className="text-right pr-1">100</div>
        </div>
      </div>
      <div className="flex justify-between text-[9px] text-slate-500 mt-1">
        <span className="text-red-400/80">Oversold (&lt;30)</span>
        <span className="text-amber-400/80">Neutral (30-50)</span>
        <span className="text-emerald-400/80">Bullish (&gt;50)</span>
      </div>
    </div>
  );
}

function RsiChart({ data, triggers }) {
  // Show last ~3 years (~156 weeks) of HA RSI plus markers at past triggers
  const slice = data.slice(-180);
  const triggerDots = triggers
    .filter(t => slice.some(d => d.ts === t.ts))
    .map(t => ({ ts: t.ts, value: t.rsi }));

  return (
    <div className="h-44 mt-2 -mx-1">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={slice} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="haRsiGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#a78bfa" stopOpacity={0.25} />
              <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={['dataMin', 'dataMax']}
            tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }}
            tickFormatter={(t) => format(new Date(t), 'MMM yyyy')}
            tickLine={false}
            axisLine={false}
            minTickGap={50}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 30, 50, 70, 100]}
            tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }}
            tickLine={false}
            axisLine={false}
            width={28}
          />
          {/* Threshold lines */}
          <ReferenceLine y={30} stroke="rgba(248,113,113,0.4)" strokeDasharray="3 3" />
          <ReferenceLine y={50} stroke="rgba(52,211,153,0.4)"  strokeDasharray="3 3" />
          <ReferenceLine y={70} stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
          <Tooltip
            cursor={{ stroke: 'rgba(255,255,255,0.18)', strokeWidth: 1, strokeDasharray: '3 3' }}
            contentStyle={{
              background: 'rgba(20,22,26,0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              fontSize: 12,
            }}
            labelFormatter={(ts) => format(new Date(ts), "'Wk of' MMM d, yyyy")}
            formatter={(v) => [`${v?.toFixed(2)}`, 'HA RSI(14)']}
          />
          <Area
            type="monotone"
            dataKey="rsi"
            stroke="#a78bfa"
            strokeWidth={1.5}
            fill="url(#haRsiGradient)"
            isAnimationActive={false}
            connectNulls
          />
          {/* Trigger markers — emerald dots at each historical trigger */}
          {triggerDots.map(t => (
            <ReferenceDot
              key={t.ts}
              x={t.ts}
              y={t.value}
              r={5}
              fill="#34d399"
              stroke="#0f172a"
              strokeWidth={2}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function TriggerHistory({ triggers, latestPrice }) {
  return (
    <div className="mt-5">
      <div className="text-xs font-semibold uppercase tracking-widest text-slate-300 flex items-center gap-2 mb-2">
        <Zap className="w-3.5 h-3.5 text-emerald-400" />
        Past triggers ({triggers.length})
      </div>
      <div className="space-y-2">
        {triggers.map((t, i) => {
          const isLatest = i === triggers.length - 1;
          const gainToHigh = t.followingHighPrice && t.priceAtTrigger
            ? (t.followingHighPrice / t.priceAtTrigger) - 1
            : null;
          const weeksToHigh = t.followingHighDate
            ? differenceInWeeks(t.followingHighDate, t.date)
            : null;
          const gainToToday = isLatest && latestPrice && t.priceAtTrigger
            ? (latestPrice / t.priceAtTrigger) - 1
            : null;
          // How much price climbed from the cycle low to the trigger bar —
          // the "off the bottom" gain by the time the rule confirmed.
          const cycleLow = t.cycleLow;
          const gainOffLow = cycleLow && t.priceAtTrigger
            ? (t.priceAtTrigger / cycleLow.low) - 1
            : null;
          // For each historical trigger: did price ever revisit the cycle
          // low? `followingLowDrawdown` is computed against the trigger
          // price, not the cycle low — if it's smaller (in absolute terms)
          // than gainOffLow, we know the cycle low held.
          const cycleLowHeld = cycleLow && t.followingLowDrawdown != null
            ? (t.priceAtTrigger * (1 + t.followingLowDrawdown)) > cycleLow.low
            : (cycleLow && t.followingLowDrawdown == null);

          return (
            <div
              key={t.ts}
              className="rounded-lg border border-white/10 bg-slate-950/40 px-3 py-2.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-2 text-xs"
            >
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">
                  Wk of {format(t.date, 'MMM d, yyyy')}
                </div>
                <div className="text-slate-400 mt-0.5">
                  RSI{' '}
                  <span className="font-mono tabular-nums text-slate-200">
                    {t.rsi.toFixed(1)}
                  </span>
                  {' '}· BTC{' '}
                  <span className="font-mono tabular-nums text-slate-200">
                    ${Math.round(t.priceAtTrigger).toLocaleString()}
                  </span>
                </div>
                {t.dipDate && (
                  <div className="mt-1 text-[10px] text-slate-500 font-mono">
                    <span className="text-red-400">Sub-30:</span>{' '}
                    {format(t.dipDate, 'MMM d, yyyy')}
                    {' → '}
                    <span className="text-emerald-400">Above-50:</span>{' '}
                    {format(t.date, 'MMM d, yyyy')}
                  </div>
                )}
              </div>
              {cycleLow && (
                <div className="text-slate-400">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    Cycle low {cycleLowHeld ? '✓ held' : ''}
                  </div>
                  <div className="font-mono tabular-nums">
                    <span className="text-amber-200">
                      ${Math.round(cycleLow.low).toLocaleString()}
                    </span>
                    {' '}· wk of {format(cycleLow.date, 'MMM d, yyyy')}
                  </div>
                  {gainOffLow != null && (
                    <div className="text-[10px] text-slate-500 font-mono">
                      Trigger fired{' '}
                      <span className="text-emerald-300">
                        +{Math.round(gainOffLow * 100)}%
                      </span>{' '}
                      off the low
                    </div>
                  )}
                </div>
              )}
              {gainToHigh != null && weeksToHigh != null && (
                <div className="text-slate-400">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Following high</div>
                  <div className="font-mono tabular-nums">
                    <span className="text-emerald-300">+{Math.round(gainToHigh * 100)}%</span>
                    {' '}({weeksToHigh}w later) · ${Math.round(t.followingHighPrice).toLocaleString()}
                  </div>
                </div>
              )}
              {gainToToday != null && (
                <div className="text-slate-400">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">From trigger to today</div>
                  <div className="font-mono tabular-nums text-emerald-300">
                    {gainToToday >= 0 ? '+' : ''}{Math.round(gainToToday * 100)}%
                  </div>
                </div>
              )}
              {!isLatest && t.followingLowDrawdown != null && (
                <div className="text-slate-400">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Worst drawdown after</div>
                  <div className="font-mono tabular-nums text-red-300">
                    {Math.round(t.followingLowDrawdown * 100)}%
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <section className="rounded-2xl border border-white/10 bg-slate-900/60 p-4 md:p-6 animate-pulse">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-5 h-5 rounded bg-slate-800" />
        <div className="h-5 w-72 rounded bg-slate-800" />
      </div>
      <div className="h-20 rounded bg-slate-800 mt-4" />
      <div className="h-7 rounded bg-slate-800 mt-3" />
      <div className="h-44 rounded bg-slate-800 mt-3" />
    </section>
  );
}

// ─── State definitions ─────────────────────────────────────────────────────

const STATE_CONFIG = {
  INACTIVE: {
    Icon:  Hourglass,
    label: 'Inactive',
    text:  'text-slate-300',
    ring:  'border-slate-600/40',
    glow:  'bg-slate-500/5',
    sub:   'No oversold dip in the recent past — setup not in play',
  },
  ARMED: {
    Icon:  AlertTriangle,
    label: 'Armed',
    text:  'text-amber-300',
    ring:  'border-amber-500/40',
    glow:  'bg-amber-500/5',
    sub:   'RSI dipped under 30 — waiting for a close above 50 to confirm',
  },
  TRIGGERED: {
    Icon:  TrendingUp,
    label: 'Triggered',
    text:  'text-emerald-300',
    ring:  'border-emerald-500/40',
    glow:  'bg-emerald-500/10',
    sub:   'RSI closed above 50 after dipping under 30 — historical bottom signal',
  },
};

// ─── Math: Heikin Ashi → RSI → trigger detection ───────────────────────────

const RSI_PERIOD = 14;
// No timeout on the armed window for trigger detection — once RSI dips
// below 30, the system stays armed until RSI closes above 50 (matching
// TradingView's behavior). The 2014-2015 bear market took 20+ weeks
// from sub-30 to above-50 and is a valid historical trigger.
// For the live "current state" display, we look back up to 52 weeks
// to determine whether the system is currently armed.
const ARMED_WINDOW_WEEKS = 52;

function analyseWeekly(bars) {
  if (!Array.isArray(bars) || bars.length < RSI_PERIOD + 5) return null;

  // 1. Convert to Heikin Ashi candles
  const ha = toHeikinAshi(bars);
  // 2. RSI(14) on HA closes — Wilder's smoothing
  const rsi = computeRsi(ha.map(b => b.close), RSI_PERIOD);
  // 3. Detect every historical trigger (with cycle-low data for each)
  const triggers = detectTriggers(ha, rsi, bars);
  // 4. Determine current state
  const last = rsi.length - 1;
  const currentRsi = rsi[last];
  const currentBar = bars[last] ?? ha[last];  // raw BTC bar for accurate price

  // Walk back ARMED_WINDOW_WEEKS to see if there was a sub-30 dip
  const armWindowStart = Math.max(0, last - ARMED_WINDOW_WEEKS);
  let dipIndex = -1;
  for (let i = last; i >= armWindowStart; i--) {
    if (Number.isFinite(rsi[i]) && rsi[i] < 30) { dipIndex = i; break; }
  }

  // Was the most recent trigger inside this dip's window?
  const recentTrigger = triggers[triggers.length - 1];
  const recentTriggerInArmWindow = recentTrigger
    && recentTrigger.barIndex >= armWindowStart;

  let state;
  let weeksSinceArmed = null;
  let weeksSinceLastTrigger = null;
  let armDipDate = null;
  // Cycle low for the active armed/triggered window — the lowest BTC price
  // seen between the sub-30 dip and now (or between the dip and the trigger
  // bar, if already triggered). This is the "where the cycle bottomed" data
  // point users actually want to see.
  let activeWindowLow = null;
  if (recentTriggerInArmWindow) {
    state = 'TRIGGERED';
    weeksSinceLastTrigger = last - recentTrigger.barIndex;
  } else if (dipIndex >= 0 && currentRsi < 50) {
    state = 'ARMED';
    // Find the dip *start* — walk back from dipIndex through the consecutive
    // sub-30 streak to the first bar in the run, so "cycle low so far" spans
    // the whole oversold episode rather than just the most recent sub-30 bar.
    let firstSub30 = dipIndex;
    for (let i = dipIndex; i >= 0; i--) {
      if (Number.isFinite(rsi[i]) && rsi[i] < 30) firstSub30 = i;
      else if (i < firstSub30) break;
    }
    weeksSinceArmed = last - firstSub30;
    armDipDate = bars[firstSub30]?.date ?? null;
    // Cap the lookback at 52 weeks so an old, very-low historical bar from
    // a prior cycle doesn't masquerade as the "current cycle low".
    const lookbackStart = Math.max(firstSub30, last - CYCLE_LOW_LOOKBACK_WEEKS);
    activeWindowLow = findLowestBar(bars, lookbackStart, last);
  } else if (recentTrigger) {
    state = 'INACTIVE';
    weeksSinceLastTrigger = last - recentTrigger.barIndex;
  } else {
    state = 'INACTIVE';
  }

  // Build chart data (last ~180 weeks) — { ts, rsi }
  const chartData = ha.map((b, i) => ({
    ts: b.date.getTime(),
    rsi: rsi[i],
  })).filter(d => Number.isFinite(d.rsi));

  return {
    state, currentRsi, currentBar, triggers, chartData,
    weeksSinceArmed, weeksSinceLastTrigger, activeWindowLow, armDipDate,
  };
}

// Find the lowest BTC bar in [startIdx, endIdx] inclusive.
// Returns { date, low, close, barIndex } — using the raw `low` from the
// week's actual price action, not the HA-smoothed value.
function findLowestBar(bars, startIdx, endIdx) {
  let bestIdx = startIdx;
  let bestLow = bars[startIdx]?.low ?? Infinity;
  for (let i = startIdx; i <= endIdx; i++) {
    const b = bars[i];
    if (!b || !Number.isFinite(b.low)) continue;
    if (b.low < bestLow) { bestLow = b.low; bestIdx = i; }
  }
  const b = bars[bestIdx];
  return b ? { date: b.date, low: b.low, close: b.close, barIndex: bestIdx } : null;
}

function toHeikinAshi(bars) {
  const out = [];
  for (let i = 0; i < bars.length; i++) {
    const b = bars[i];
    const haClose = (b.open + b.high + b.low + b.close) / 4;
    const haOpen = i === 0
      ? (b.open + b.close) / 2
      : (out[i - 1].open + out[i - 1].close) / 2;
    const haHigh = Math.max(b.high, haOpen, haClose);
    const haLow  = Math.min(b.low,  haOpen, haClose);
    out.push({
      date: b.date,
      open:  haOpen,
      high:  haHigh,
      low:   haLow,
      close: haClose,
    });
  }
  return out;
}

// Wilder's RSI — the standard implementation Trading View uses by default.
// Returns an array of length = closes.length, with NaN for the first
// `period` entries (insufficient data for the smoothed average).
function computeRsi(closes, period) {
  const out = new Array(closes.length).fill(NaN);
  if (closes.length < period + 1) return out;

  // Seed the average gain / loss with simple averages over the first window
  let gainSum = 0, lossSum = 0;
  for (let i = 1; i <= period; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff >= 0) gainSum += diff; else lossSum -= diff;
  }
  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  out[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  // Subsequent bars use Wilder's smoothed average
  for (let i = period + 1; i < closes.length; i++) {
    const diff = closes[i] - closes[i - 1];
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + gain) / period;
    avgLoss = (avgLoss * (period - 1) + loss) / period;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

// How far back from a trigger we look for the "cycle low". A typical BTC
// drawdown from peak to trough is 8-14 months; capping the cycle-low window
// at ~52 weeks prevents one extended sub-30 streak (which can span multiple
// cycles, like 2014-2019 in Yahoo's history) from reporting a low that
// belongs to the prior cycle. Traders intuitively read "cycle low" as the
// bottom of *this* drawdown, not the all-time low of every prior dip.
const CYCLE_LOW_LOOKBACK_WEEKS = 52;

// Detect every historical trigger: the first bar where RSI > 50 after a
// sub-30 dip. No time limit — once RSI dips below 30, the system stays
// armed indefinitely until RSI closes above 50 (matching TradingView's
// indicator behavior). After a trigger fires, we require the next "dip
// below 30" before another trigger can arm — this prevents one dip from
// generating multiple triggers as RSI oscillates above and below 50.
//
// Each trigger also records the cycle-window low — the lowest raw BTC
// price in the trailing 52 weeks before the trigger fired. That's the
// answer to "what was the cycle bottom?".
function detectTriggers(haBars, rsi, rawBars) {
  const out = [];
  let armed = false;
  let firstDipIndex = -1;   // start of the current sub-30 streak

  for (let i = 0; i < rsi.length; i++) {
    const v = rsi[i];
    if (!Number.isFinite(v)) continue;
    if (v < 30) {
      if (!armed) firstDipIndex = i;  // first bar of a fresh oversold streak
      armed = true;
    } else if (armed && v > 50) {
      const triggerBar = rawBars[i] ?? haBars[i];
      // Cycle low = lowest raw BTC price in [max(firstDip, i - 52), i].
      // The 52w cap keeps the "low" tied to the current drawdown rather
      // than letting a long armed streak span multiple cycles.
      const lookbackStart = Math.max(firstDipIndex, i - CYCLE_LOW_LOOKBACK_WEEKS);
      const cycleLow = findLowestBar(rawBars, lookbackStart, i);
      const dipBar = rawBars[firstDipIndex] ?? haBars[firstDipIndex];
      out.push({
        ts: triggerBar.date.getTime(),
        date: triggerBar.date,
        dipDate: dipBar?.date ?? null,
        rsi: v,
        priceAtTrigger: triggerBar.close,
        barIndex: i,
        cycleLow,                   // { date, low, close, barIndex }
        ...computeFollowing(rawBars, i),
      });
      armed = false;
      firstDipIndex = -1;
    }
  }
  return out;
}

// For a given trigger index, find the highest price reached before the next
// trigger (or end of data), and the worst drawdown along the way. Uses raw
// bars (not HA-smoothed) so price figures match actual BTC market data.
function computeFollowing(rawBars, triggerIdx) {
  let highIdx = triggerIdx;
  let highPrice = rawBars[triggerIdx].high;
  let lowestSinceTrigger = rawBars[triggerIdx].low;
  for (let i = triggerIdx + 1; i < rawBars.length; i++) {
    const b = rawBars[i];
    if (b.high > highPrice) { highPrice = b.high; highIdx = i; }
    if (b.low < lowestSinceTrigger) { lowestSinceTrigger = b.low; }
  }
  const triggerPrice = rawBars[triggerIdx].close;
  const drawdown = (lowestSinceTrigger - triggerPrice) / triggerPrice;
  return {
    followingHighPrice: highPrice,
    followingHighDate:  rawBars[highIdx]?.date,
    followingLowDrawdown: drawdown < 0 ? drawdown : null,
  };
}
